/**
 * Post Lead to Marketplace
 * Multi-step form to list a lead on the marketplace
 * Redesigned to match LeadFlow prototype style
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { Slider } from 'primereact/slider';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Skeleton } from 'primereact/skeleton';
import { Paginator } from 'primereact/paginator';
import { Message } from 'primereact/message';
import { Dropdown } from 'primereact/dropdown';
import {
    marketplaceService,
    formatLoanAmount,
    getLoanProductConfig
} from '../../services/marketplaceService';
import NavigationTabMenu from '../../components/common/NavigationTabMenu';
import axiosInstance from '../../config/axios.config';

// Sell Leads section tabs
const SELL_LEADS_TABS = [
    { label: 'My Listings', icon: 'pi pi-list', route: '/app/marketplace/my-listings' },
    { label: 'Buyer Enquiries', icon: 'pi pi-inbox', route: '/app/marketplace/received-interests' },
    { label: 'Post My Lead', icon: 'pi pi-plus', route: '/app/marketplace/post' }
];

// Custom styles for the page - Compact design for 100% zoom
const pageStyles = `
    .post-lead-page {
        max-width: 1000px;
        margin: 0 auto;
        font-size: 0.8125rem;
    }

    /* Custom Wizard Steps - Compact */
    .wizard-steps {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0;
        margin-bottom: 1rem;
        padding: 0.75rem;
        background: var(--surface-card);
        border-radius: 10px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    }

    .wizard-step {
        display: flex;
        align-items: center;
        gap: 0.375rem;
    }

    .wizard-step-number {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 0.75rem;
        background: var(--surface-200);
        color: var(--text-color-secondary);
        transition: all 0.3s ease;
    }

    .wizard-step.active .wizard-step-number {
        background: var(--primary-color);
        color: white;
        box-shadow: 0 2px 6px rgba(99, 102, 241, 0.35);
    }

    .wizard-step.completed .wizard-step-number {
        background: #22C55E;
        color: white;
    }

    .wizard-step-label {
        font-size: 0.8125rem;
        font-weight: 500;
        color: var(--text-color-secondary);
    }

    .wizard-step.active .wizard-step-label,
    .wizard-step.completed .wizard-step-label {
        color: var(--text-color);
    }

    .wizard-step-connector {
        width: 40px;
        height: 2px;
        background: var(--surface-200);
        margin: 0 0.375rem;
    }

    .wizard-step-connector.completed {
        background: #22C55E;
    }

    @media (max-width: 576px) {
        .wizard-steps {
            padding: 0.5rem;
        }
        .wizard-step-label {
            display: none;
        }
        .wizard-step-connector {
            width: 30px;
        }
    }

    /* Lead Selection Card - Compact */
    .lead-select-card {
        background: var(--surface-card);
        border: 1px solid var(--surface-200);
        border-radius: 6px;
        padding: 0.625rem;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .lead-select-card:hover {
        border-color: var(--surface-400);
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    }

    .lead-select-card.selected {
        border-color: var(--primary-color);
        border-width: 2px;
        background: var(--primary-50);
        box-shadow: 0 2px 8px rgba(99, 102, 241, 0.12);
        padding: calc(0.625rem - 1px);
    }

    .lead-select-card-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: 0.375rem;
    }

    .lead-select-card-name {
        font-weight: 600;
        font-size: 0.875rem;
        color: var(--text-color);
    }

    .lead-select-card-radio {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 2px solid var(--surface-400);
        flex-shrink: 0;
    }

    .lead-select-card.selected .lead-select-card-radio {
        border-color: var(--primary-color);
        background: var(--primary-color);
        position: relative;
    }

    .lead-select-card.selected .lead-select-card-radio::after {
        content: '';
        position: absolute;
        width: 6px;
        height: 6px;
        background: white;
        border-radius: 50%;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    .lead-select-card-details {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 0.375rem;
    }

    @media (max-width: 576px) {
        .lead-select-card-details {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    .lead-select-card-detail-label {
        font-size: 0.625rem;
        color: var(--text-color-secondary);
        margin-bottom: 0.0625rem;
        display: block;
        text-transform: uppercase;
        letter-spacing: 0.02em;
    }

    .lead-select-card-detail-value {
        font-size: 0.75rem;
        font-weight: 500;
        color: var(--text-color);
    }

    /* Quick Chips - Compact */
    .quick-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 0.375rem;
    }

    .quick-chip {
        padding: 0.375rem 0.75rem;
        border-radius: 16px;
        border: 1px solid var(--surface-300);
        background: var(--surface-card);
        color: var(--text-color);
        font-size: 0.8125rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .quick-chip:hover {
        border-color: var(--primary-color);
        background: var(--primary-50);
    }

    .quick-chip.active {
        background: var(--primary-color);
        border-color: var(--primary-color);
        color: white;
    }

    /* Scope Card - Compact */
    .scope-card {
        background: var(--surface-card);
        border: 1px solid var(--surface-200);
        border-radius: 6px;
        padding: 0.5rem 0.625rem;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .scope-card:hover {
        border-color: var(--surface-400);
    }

    .scope-card.selected {
        border-color: var(--primary-color);
        border-width: 2px;
        background: var(--primary-50);
        padding: calc(0.5rem - 1px) calc(0.625rem - 1px);
    }

    /* Form Group - Compact */
    .form-group {
        margin-bottom: 1rem;
    }

    .form-label {
        display: block;
        font-weight: 600;
        font-size: 0.75rem;
        color: var(--text-color);
        margin-bottom: 0.25rem;
    }

    .form-label.required::after {
        content: ' *';
        color: #EF4444;
    }

    .form-help {
        font-size: 0.6875rem;
        color: var(--text-color-secondary);
        margin-top: 0.25rem;
    }

    /* Summary Sidebar - Compact */
    .summary-sidebar {
        position: sticky;
        top: 80px;
    }

    .summary-sidebar h4 {
        font-size: 0.875rem;
    }

    @media (max-width: 991px) {
        .summary-sidebar {
            position: static;
        }
    }

    /* Card and Section Headers - Compact */
    .post-lead-page h3 {
        font-size: 0.9375rem !important;
    }

    .post-lead-page h4 {
        font-size: 0.8125rem !important;
    }

    /* Reduce overall card padding */
    .post-lead-page .surface-card.p-4 {
        padding: 0.875rem !important;
    }

    /* Tighter section spacing */
    .post-lead-page .mb-4 {
        margin-bottom: 0.875rem !important;
    }

    .post-lead-page .mb-3 {
        margin-bottom: 0.625rem !important;
    }

    /* Compact grid gaps */
    .post-lead-page .gap-3 {
        gap: 0.625rem !important;
    }

    .post-lead-page .gap-2 {
        gap: 0.375rem !important;
    }

    /* Success Screen - Compact */
    .success-screen {
        text-align: center;
        padding: 1.5rem 1rem;
    }

    .success-icon {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: #DCFCE7;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 0.75rem;
    }

    .success-icon i {
        font-size: 1.75rem;
        color: #22C55E;
    }

    .success-screen h2 {
        font-size: 1.125rem !important;
    }

    .success-screen p {
        font-size: 0.875rem !important;
    }

    /* Override PrimeReact components for compact design */
    .post-lead-page .surface-card {
        padding: 1rem !important;
    }

    .post-lead-page .p-inputtext {
        padding: 0.5rem 0.75rem;
        font-size: 0.875rem;
    }

    /* Fix search input icon overlap */
    .post-lead-page .p-input-icon-left > .p-inputtext {
        padding-left: 2.25rem;
    }

    .post-lead-page .p-input-icon-left > i {
        left: 0.75rem;
        color: var(--text-color-secondary);
    }

    .post-lead-page .p-button {
        padding: 0.5rem 1rem;
        font-size: 0.875rem;
    }

    .post-lead-page .p-button.p-button-sm {
        padding: 0.375rem 0.75rem;
        font-size: 0.8125rem;
    }

    .post-lead-page .p-slider {
        height: 6px;
    }

    .post-lead-page .p-slider .p-slider-handle {
        width: 16px;
        height: 16px;
        margin-top: -5px;
    }

    .post-lead-page .p-tag {
        padding: 0.25rem 0.5rem;
        font-size: 0.75rem;
    }

    .post-lead-page .p-paginator {
        padding: 0.5rem;
    }

    .post-lead-page .p-paginator .p-paginator-element {
        min-width: 2rem;
        height: 2rem;
    }

    /* Scrollable Lead List with Fixed Height */
    .lead-list-container {
        max-height: 400px;
        overflow-y: auto;
        margin-bottom: 1rem;
    }

    /* Navigation Buttons - Inside card at bottom */
    .nav-buttons-container {
        padding-top: 0.75rem;
        border-top: 1px solid var(--surface-200);
        margin-top: auto;
    }
`;

const PostLead = () => {
    const navigate = useNavigate();
    const toast = useRef(null);

    // Note: pageStyles rendered inline in JSX to prevent CLS from delayed style injection

    // Step management
    const [activeStep, setActiveStep] = useState(0);

    // Step 1: Lead selection with pagination
    const [leads, setLeads] = useState([]);
    const [loadingLeads, setLoadingLeads] = useState(true);
    const [selectedLead, setSelectedLead] = useState(null);

    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

    // Pagination state
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Step 2: Configuration
    const [scope, setScope] = useState('public');
    const [commission, setCommission] = useState(2.5);
    const [maxSpots, setMaxSpots] = useState(3);
    const [expiresAt, setExpiresAt] = useState(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [dataAccurate, setDataAccurate] = useState(false);

    // Completeness preview (blueprint §11) — what grade this lead will
    // post as + what's missing. Non-blocking nudge when low.
    const [quality, setQuality] = useState(null);
    const [loadingQuality, setLoadingQuality] = useState(false);

    // Confirmed post-time location (blueprint §13.10) — prefilled from the
    // lead, editable. Pincode auto-fills City/State (same pattern as the
    // profile BasicInfoStep). Drives city_tier + the bot's location filter.
    const [postLocation, setPostLocation] = useState({ state: '', city: '', pincode: '' });
    const [stateOptions, setStateOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [locationLoading, setLocationLoading] = useState(false);

    // Step 3: Posting
    const [posting, setPosting] = useState(false);
    const [postSuccess, setPostSuccess] = useState(false);
    const [newListingId, setNewListingId] = useState(null);

    // Quick expiry options
    const expiryOptions = [
        { label: '7 days', days: 7 },
        { label: '14 days', days: 14 },
        { label: '30 days', days: 30 }
    ];

    // Helper functions
    const getLeadName = (lead) => {
        if (lead.full_name) return lead.full_name;
        if (lead.first_name || lead.last_name) {
            return `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
        }
        return lead.name || 'Unknown';
    };

    const getLeadLoanAmount = (lead) => {
        return lead.loan_amount_required || lead.loan_amount || lead.amount || 0;
    };

    const getLeadProductType = (lead) => {
        return lead.product_type_detailed || lead.product_type || 'personal_loan';
    };

    // Calculate estimated commission
    const getEstimatedCommission = () => {
        if (!selectedLead) return 0;
        return Math.round((getLeadLoanAmount(selectedLead) * commission) / 100);
    };

    // Calculate days until expiry
    const getDaysUntilExpiry = () => {
        const now = new Date();
        const expiry = new Date(expiresAt);
        const diffTime = expiry - now;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    // Centralized fetch function for leads
    const fetchLeads = async () => {
        setLoadingLeads(true);
        try {
            const currentPage = Math.floor(first / rows) + 1;
            const params = {
                status: 'active',
                not_listed: 'true',
                per_page: rows,
                page: currentPage
            };

            if (appliedSearchTerm && appliedSearchTerm.trim() !== '') {
                params.search = appliedSearchTerm.trim();
            }

            const response = await axiosInstance.get('/leads', { params });

            if (response.data.success) {
                const leadsData = response.data.data || [];
                const pagination = response.data.pagination || {};
                setLeads(leadsData);
                setTotalRecords(pagination.total || 0);
            } else {
                setLeads([]);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Failed to fetch leads:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load your leads'
            });
            setLeads([]);
            setTotalRecords(0);
        } finally {
            setLoadingLeads(false);
        }
    };

    useEffect(() => {
        fetchLeads();
    }, [first, rows, appliedSearchTerm]);

    // Fetch the completeness preview when a lead is chosen / changed.
    // Same scorer the post uses → the preview matches the posted grade.
    useEffect(() => {
        let cancelled = false;
        if (!selectedLead?.id) {
            setQuality(null);
            return;
        }
        setLoadingQuality(true);
        marketplaceService.previewQuality(selectedLead.id)
            .then((res) => { if (!cancelled) setQuality(res?.data || null); })
            .catch(() => { if (!cancelled) setQuality(null); })
            .finally(() => { if (!cancelled) setLoadingQuality(false); });
        return () => { cancelled = true; };
    }, [selectedLead?.id]);

    // Load the canonical State list once (same master the app uses).
    useEffect(() => {
        let cancelled = false;
        axiosInstance.get('/location/states')
            .then((res) => {
                if (!cancelled && res?.data?.success) setStateOptions(res.data.data || []);
            })
            .catch(() => { /* non-blocking */ });
        return () => { cancelled = true; };
    }, []);

    // Load cities for a state (cascading). Returns the list too.
    const loadCities = useCallback(async (state) => {
        if (!state) { setCityOptions([]); return []; }
        try {
            const res = await axiosInstance.get(`/location/cities/by-state/${encodeURIComponent(state)}`);
            const list = res?.data?.success ? (res.data.data || []) : [];
            setCityOptions(list);
            return list;
        } catch {
            setCityOptions([]);
            return [];
        }
    }, []);

    // Prefill State + City from the selected lead. If the lead has a
    // pincode we resolve it SILENTLY (no pincode field shown) to
    // pre-select both dropdowns; else fall back to the lead's state/city.
    // The resolved pincode is kept ONLY to send for best tier accuracy —
    // it is dropped the moment the user changes a dropdown manually.
    useEffect(() => {
        let cancelled = false;
        if (!selectedLead?.id) {
            setPostLocation({ state: '', city: '', pincode: '' });
            setCityOptions([]);
            return;
        }
        const pin = selectedLead.pincode ? String(selectedLead.pincode).trim() : '';
        const apply = async (state, city, pincode) => {
            if (cancelled) return;
            if (state) await loadCities(state);
            if (!cancelled) setPostLocation({ state: state || '', city: city || '', pincode: pincode || '' });
        };
        if (/^\d{6}$/.test(pin)) {
            setLocationLoading(true);
            axiosInstance.get(`/location/pincode/${pin}`)
                .then((res) => {
                    const d = res?.data?.data;
                    if (res?.data?.success && d) {
                        return apply(d.state || selectedLead.state, d.city || d.district || selectedLead.city, pin);
                    }
                    return apply(selectedLead.state, selectedLead.city, '');
                })
                .catch(() => apply(selectedLead.state, selectedLead.city, ''))
                .finally(() => { if (!cancelled) setLocationLoading(false); });
        } else {
            apply(selectedLead.state, selectedLead.city, '');
        }
        return () => { cancelled = true; };
    }, [selectedLead?.id, loadCities]);

    // User picks a State → reset City, load that state's cities, and drop
    // any prefilled pincode (it no longer matches → server resolves tier
    // by city instead).
    const onStateChange = (state) => {
        setPostLocation({ state, city: '', pincode: '' });
        loadCities(state);
    };
    const onCityChange = (city) => {
        setPostLocation((p) => ({ ...p, city, pincode: '' }));
    };

    const handleSearchApply = () => {
        setFirst(0);
        setAppliedSearchTerm(searchTerm);
    };

    const handleSearchClear = () => {
        setSearchTerm('');
        setFirst(0);
        setAppliedSearchTerm('');
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearchApply();
        }
    };

    const onPageChange = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    const setQuickExpiry = (days) => {
        setExpiresAt(new Date(Date.now() + days * 24 * 60 * 60 * 1000));
    };

    // Handle post
    const handlePost = async () => {
        if (!selectedLead) return;
        setPosting(true);
        try {
            const response = await marketplaceService.postLead({
                leadId: selectedLead.id,
                scope,
                commissionPercentage: commission,
                maxSpots,
                expiresAt: expiresAt.toISOString(),
                // Confirmed post-time location (blueprint §13.10)
                state: postLocation.state?.trim() || undefined,
                city: postLocation.city?.trim() || undefined,
                pincode: postLocation.pincode?.trim() || undefined,
            });
            if (response.success) {
                setPostSuccess(true);
                setNewListingId(response.data?.marketplace_lead?.id);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success!',
                    detail: 'Your lead has been posted to the marketplace'
                });
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to post lead';

            if (errorMessage.includes('already active') || errorMessage.includes('already listed')) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Already Active',
                    detail: errorMessage,
                    life: 5000
                });
            } else if (errorMessage.includes('Duplicate entry') || errorMessage.includes('unique_source_lead')) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Already Listed',
                    detail: 'This lead has already been posted to the marketplace.',
                    life: 5000
                });
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: errorMessage
                });
            }
        } finally {
            setPosting(false);
        }
    };

    // Navigation
    const canProceed = () => {
        if (activeStep === 0) return !!selectedLead;
        if (activeStep === 1) return termsAccepted && dataAccurate;
        return true;
    };

    const nextStep = () => {
        if (canProceed() && activeStep < 2) {
            setActiveStep(activeStep + 1);
        }
    };

    const prevStep = () => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        }
    };

    // Wizard Steps Component
    const WizardSteps = () => (
        <div className="wizard-steps">
            <div className={`wizard-step ${activeStep > 0 ? 'completed' : activeStep === 0 ? 'active' : ''}`}>
                <div className="wizard-step-number">
                    {activeStep > 0 ? <i className="pi pi-check text-xs" /> : '1'}
                </div>
                <span className="wizard-step-label">Select Lead</span>
            </div>
            <div className={`wizard-step-connector ${activeStep > 0 ? 'completed' : ''}`} />
            <div className={`wizard-step ${activeStep > 1 ? 'completed' : activeStep === 1 ? 'active' : ''}`}>
                <div className="wizard-step-number">
                    {activeStep > 1 ? <i className="pi pi-check text-xs" /> : '2'}
                </div>
                <span className="wizard-step-label">Configure</span>
            </div>
            <div className={`wizard-step-connector ${activeStep > 1 ? 'completed' : ''}`} />
            <div className={`wizard-step ${activeStep === 2 ? 'active' : ''}`}>
                <div className="wizard-step-number">3</div>
                <span className="wizard-step-label">Review</span>
            </div>
        </div>
    );

    // Lead Select Card Component
    const LeadSelectCard = ({ lead, isSelected, onSelect }) => {
        const productConfig = getLoanProductConfig(getLeadProductType(lead));
        const loanAmount = getLeadLoanAmount(lead);
        const leadName = getLeadName(lead);

        return (
            <div
                className={`lead-select-card ${isSelected ? 'selected' : ''}`}
                onClick={() => onSelect(lead)}
            >
                <div className="lead-select-card-header">
                    <div>
                        <div className="lead-select-card-name">{leadName}</div>
                        <div className="text-xs text-500">ID: {lead.id}</div>
                    </div>
                    <div className="lead-select-card-radio" />
                </div>
                <div className="lead-select-card-details">
                    <div>
                        <span className="lead-select-card-detail-label">Loan Type</span>
                        <div className="lead-select-card-detail-value">{productConfig.label}</div>
                    </div>
                    <div>
                        <span className="lead-select-card-detail-label">Amount</span>
                        <div className="lead-select-card-detail-value text-green-600">{formatLoanAmount(loanAmount)}</div>
                    </div>
                    {/* Only render fields that actually have a value — no
                        "N/A" filler (dynamic leads leave many static
                        columns empty). */}
                    {lead.employment_type && (
                        <div>
                            <span className="lead-select-card-detail-label">Employment</span>
                            <div className="lead-select-card-detail-value capitalize">
                                {lead.employment_type.replace(/_/g, ' ')}
                            </div>
                        </div>
                    )}
                    {lead.cibil_score && (
                        <div>
                            <span className="lead-select-card-detail-label">CIBIL</span>
                            <div className={`lead-select-card-detail-value ${lead.cibil_score >= 700 ? 'text-green-600' : 'text-orange-500'}`}>
                                {lead.cibil_score}
                            </div>
                        </div>
                    )}
                    {lead.city && (
                        <div>
                            <span className="lead-select-card-detail-label">City</span>
                            <div className="lead-select-card-detail-value">{lead.city}</div>
                        </div>
                    )}
                    {lead.phone && (
                        <div>
                            <span className="lead-select-card-detail-label">Phone</span>
                            <div className="lead-select-card-detail-value">{lead.phone}</div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Loading skeleton
    const renderLeadsSkeleton = () => (
        <div className="flex flex-column gap-2">
            {[1, 2, 3].map(i => (
                <div key={i} className="surface-card border-round p-2 border-1 border-200">
                    <div className="flex align-items-start justify-content-between mb-2">
                        <div>
                            <Skeleton width="120px" height="1rem" className="mb-1" />
                            <Skeleton width="60px" height="0.75rem" />
                        </div>
                        <Skeleton shape="circle" size="16px" />
                    </div>
                    <div className="grid">
                        {[1, 2, 3, 4, 5, 6].map(j => (
                            <div key={j} className="col-4">
                                <Skeleton width="50px" height="0.625rem" className="mb-1" />
                                <Skeleton width="70px" height="0.875rem" />
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );

    // Empty state
    const renderEmptyState = () => (
        <div className="flex flex-column align-items-center justify-content-center py-4">
            <i className="pi pi-inbox text-3xl text-300 mb-2" />
            <h4 className="text-600 mb-1 mt-0 text-base">No Leads Available</h4>
            <p className="text-500 text-center text-sm mb-3 m-0">
                You don't have any leads that can be listed on the marketplace.
            </p>
            <Button
                label="Go to Leads"
                icon="pi pi-arrow-right"
                className="p-button-outlined p-button-sm"
                onClick={() => navigate('/app/leads')}
            />
        </div>
    );

    // Summary Sidebar Component
    const SummarySidebar = () => (
        <div className="summary-sidebar surface-card border-round shadow-1 p-3">
            <h4 className="m-0 mb-2 font-semibold text-sm">Listing Summary</h4>

            {selectedLead ? (
                <>
                    <div className="border-bottom-1 border-200 pb-2 mb-2">
                        <div className="text-500 text-xs mb-1">Lead</div>
                        <div className="font-semibold text-sm">{getLeadName(selectedLead)}</div>
                        <div className="text-xs text-500">
                            {getLoanProductConfig(getLeadProductType(selectedLead)).label} - {formatLoanAmount(getLeadLoanAmount(selectedLead))}
                        </div>
                    </div>

                    <div className="grid text-xs mb-2" style={{ rowGap: '0.5rem' }}>
                        <div className="col-6 text-500">Scope</div>
                        <div className="col-6 text-right">
                            <Tag
                                value={scope === 'network' ? 'Network' : 'Public'}
                                severity={scope === 'network' ? 'info' : 'success'}
                                className="text-xs"
                                style={{ padding: '0.15rem 0.4rem', fontSize: '0.6875rem' }}
                            />
                        </div>

                        <div className="col-6 text-500">Lead Commission</div>
                        <div className="col-6 text-right font-medium text-green-600">{parseFloat(commission.toFixed(3))}%</div>

                        <div className="col-6 text-500">Est. Commission</div>
                        <div className="col-6 text-right font-medium">₹{getEstimatedCommission().toLocaleString('en-IN')}</div>

                        <div className="col-6 text-500">Max Collaborators</div>
                        <div className="col-6 text-right font-medium">{maxSpots}</div>

                        <div className="col-6 text-500">Expires</div>
                        <div className="col-6 text-right font-medium">
                            {expiresAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </div>
                    </div>
                </>
            ) : (
                <div className="text-500 text-xs">Select a lead to see summary</div>
            )}

            <div className="bg-blue-50 border-round p-2 text-xs">
                <div className="flex gap-2">
                    <i className="pi pi-info-circle text-blue-500 flex-shrink-0 text-xs" />
                    <div>
                        <div className="text-blue-700 font-medium mb-1">Privacy Notice</div>
                        <div className="text-blue-600 line-height-3">Personal details will be masked until lead deal is confirmed.</div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Success screen
    if (postSuccess) {
        return (
            <div className="post-lead-page">
                <Toast ref={toast} />
                <div className="success-screen">
                    <div className="success-icon">
                        <i className="pi pi-check" />
                    </div>
                    <h2 className="text-900 font-bold mb-2 mt-0">Lead Posted Successfully!</h2>
                    <p className="text-600 mb-4">
                        Your lead is now live on the {scope === 'network' ? 'Network' : 'Public'} marketplace.
                    </p>
                    <div className="flex flex-wrap justify-content-center gap-3">
                        <Button
                            label="View Listing"
                            icon="pi pi-eye"
                            onClick={() => navigate(`/app/marketplace/listing/${newListingId}`)}
                        />
                        <Button
                            label="Post Another"
                            icon="pi pi-plus"
                            className="p-button-outlined"
                            onClick={() => {
                                setPostSuccess(false);
                                setSelectedLead(null);
                                setActiveStep(0);
                            }}
                        />
                        <Button
                            label="My Listings"
                            icon="pi pi-list"
                            className="p-button-text"
                            onClick={() => navigate('/app/marketplace/my-listings')}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="post-lead-page">
            <NavigationTabMenu tabs={SELL_LEADS_TABS} />
            <style>{pageStyles}</style>
            <Toast ref={toast} />

            {/* Page Header */}
            <div className="mb-3">
                <h1 className="text-lg font-bold text-900 m-0 mb-1">Post My Lead</h1>
                <p className="text-600 text-sm m-0">List your lead on the marketplace</p>
            </div>

            {/* Wizard Steps */}
            <WizardSteps />

            <div className="grid">
                {/* Main Content */}
                <div className="col-12 lg:col-8">
                    {/* Step 1: Select Lead */}
                    {activeStep === 0 && (
                        <div className="surface-card border-round shadow-1 p-3 mb-3">
                            <div className="flex align-items-center justify-content-between mb-3">
                                <div>
                                    <h3 className="m-0 font-semibold">Select a Lead</h3>
                                    <p className="text-500 text-xs m-0 mt-1">Choose from your active leads that are not already listed</p>
                                </div>
                                {totalRecords > 0 && (
                                    <Tag value={`${totalRecords} available`} severity="info" style={{ fontSize: '0.6875rem', padding: '0.2rem 0.5rem' }} />
                                )}
                            </div>

                            {/* Search */}
                            <div className="mb-3">
                                <div className="flex gap-2">
                                    <span className="p-input-icon-left flex-1">
                                        <i className="pi pi-search" />
                                        <InputText
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            onKeyDown={handleSearchKeyDown}
                                            placeholder="Search by name, phone, or city..."
                                            className="w-full"
                                        />
                                    </span>
                                    <Button
                                        icon="pi pi-search"
                                        onClick={handleSearchApply}
                                        className="p-button-outlined"
                                    />
                                    {appliedSearchTerm && (
                                        <Button
                                            icon="pi pi-times"
                                            onClick={handleSearchClear}
                                            className="p-button-outlined p-button-secondary"
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Leads List - Scrollable Container */}
                            <div className="lead-list-container">
                                {loadingLeads ? renderLeadsSkeleton() : leads.length === 0 ? (
                                    appliedSearchTerm ? (
                                        <div className="text-center py-4">
                                            <i className="pi pi-search text-3xl text-300 mb-2" />
                                            <h4 className="text-600 mb-1 mt-0 text-sm">No leads found</h4>
                                            <p className="text-500 m-0 mb-2 text-xs">No leads match "{appliedSearchTerm}"</p>
                                            <Button
                                                label="Clear Search"
                                                icon="pi pi-times"
                                                className="p-button-outlined p-button-sm"
                                                onClick={handleSearchClear}
                                            />
                                        </div>
                                    ) : renderEmptyState()
                                ) : (
                                    <div className="flex flex-column gap-2">
                                        {leads.map(lead => (
                                            <LeadSelectCard
                                                key={lead.id}
                                                lead={lead}
                                                isSelected={selectedLead?.id === lead.id}
                                                onSelect={setSelectedLead}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Pagination - Always rendered to prevent CLS */}
                                <div className="mt-3" style={{ visibility: totalRecords > rows ? 'visible' : 'hidden' }}>
                                    <Paginator
                                        first={first}
                                        rows={rows}
                                        totalRecords={totalRecords || 0}
                                        rowsPerPageOptions={[5, 10, 20]}
                                        onPageChange={onPageChange}
                                        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                                    />
                                </div>
                            </div>

                            {/* Navigation Buttons - Step 1 */}
                            <div className="nav-buttons-container flex justify-content-between">
                                <Button
                                    label="Back"
                                    icon="pi pi-arrow-left"
                                    className="p-button-text"
                                    onClick={prevStep}
                                    disabled={activeStep === 0}
                                />
                                <Button
                                    label="Continue"
                                    icon="pi pi-arrow-right"
                                    iconPos="right"
                                    onClick={nextStep}
                                    disabled={!canProceed()}
                                />
                            </div>
                        </div>
                    )}

                    {/* Step 2: Configure */}
                    {activeStep === 1 && selectedLead && (
                        <>
                            {/* Selected Lead Summary */}
                            <div className="surface-card border-round shadow-1 p-3 mb-3">
                                <div className="flex align-items-center justify-content-between mb-2">
                                    <h3 className="m-0 font-semibold">Selected Lead</h3>
                                    <Button
                                        label="Change"
                                        icon="pi pi-pencil"
                                        className="p-button-text p-button-sm"
                                        onClick={() => setActiveStep(0)}
                                    />
                                </div>
                                <LeadSelectCard
                                    lead={selectedLead}
                                    isSelected={true}
                                    onSelect={() => {}}
                                />
                            </div>

                            {/* Lead Location (blueprint §13.10b) — canonical
                                State → City dropdowns. Prefilled from the
                                lead (via its pincode, resolved silently — no
                                pincode field shown). Clean values so the bot
                                can filter state-wise / city-wise reliably. */}
                            <div className="surface-card border-round shadow-1 p-3 mb-3">
                                <div className="flex align-items-center justify-content-between mb-1">
                                    <h3 className="m-0 font-semibold">Lead Location</h3>
                                    {locationLoading && <i className="pi pi-spin pi-spinner text-500" />}
                                </div>
                                <p className="text-600 text-sm mt-0 mb-3">
                                    Confirm where this lead is — buyers filter by location, so pick the correct State &amp; City.
                                </p>
                                <div className="grid">
                                    <div className="col-12 md:col-6">
                                        <label className="block text-700 text-sm mb-1">State</label>
                                        <Dropdown
                                            value={postLocation.state}
                                            options={stateOptions}
                                            onChange={(e) => onStateChange(e.value)}
                                            placeholder="Select State"
                                            filter
                                            showClear
                                            className="w-full p-inputtext-sm"
                                        />
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <label className="block text-700 text-sm mb-1">City</label>
                                        <Dropdown
                                            value={postLocation.city}
                                            options={cityOptions}
                                            onChange={(e) => onCityChange(e.value)}
                                            placeholder={postLocation.state ? 'Select City' : 'Select State first'}
                                            disabled={!postLocation.state}
                                            filter
                                            editable
                                            showClear
                                            className="w-full p-inputtext-sm"
                                        />
                                    </div>
                                </div>
                                <small className="text-500 block mt-2">
                                    City tier is derived automatically from this location — no pincode needed.
                                </small>
                            </div>

                            {/* Completeness preview (blueprint §11) — what
                                this lead will post as + what's missing.
                                Non-blocking: posting stays enabled. */}
                            <div className="surface-card border-round shadow-1 p-3 mb-3">
                                <div className="flex align-items-center justify-content-between mb-2">
                                    <h3 className="m-0 font-semibold">Lead Quality Preview</h3>
                                    {loadingQuality ? (
                                        <Skeleton width="3rem" height="1.5rem" />
                                    ) : quality ? (
                                        <Tag
                                            value={`${quality.grade} · ${quality.score}/100`}
                                            severity={quality.score >= 80 ? 'success' : quality.score >= 55 ? 'info' : 'warning'}
                                        />
                                    ) : null}
                                </div>

                                {!loadingQuality && quality && (
                                    <>
                                        <p className="text-600 text-sm mt-0 mb-2">{quality.label}</p>

                                        {quality.is_low && (
                                            <Message
                                                severity="warn"
                                                className="w-full mb-2"
                                                text="This lead will post as Low quality. You can still post it — adding the items below will improve buyer trust and matching."
                                            />
                                        )}

                                        {Array.isArray(quality.top_gaps) && quality.top_gaps.length > 0 && (
                                            <div>
                                                <span className="text-700 font-medium text-sm">What would raise the score</span>
                                                <ul className="mt-1 mb-0 pl-3 text-600 text-sm">
                                                    {quality.top_gaps.map((g) => (
                                                        <li key={g.param} className="mb-1">
                                                            <span className="font-medium text-900">{String(g.param).replace(/_/g, ' ')}</span>
                                                            {' '}— {g.detail} <span className="text-500">(+{g.missing} pts)</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                )}

                                {!loadingQuality && !quality && (
                                    <p className="text-500 text-sm m-0">Quality preview unavailable for this lead.</p>
                                )}
                            </div>

                            {/* Configuration Form */}
                            <div className="surface-card border-round shadow-1 p-3 mb-3">
                                <h3 className="m-0 font-semibold mb-3">Marketplace Configuration</h3>

                                {/* Scope Selection */}
                                <div className="form-group">
                                    <label className="form-label required">Marketplace Scope</label>
                                    <div className="grid">
                                        <div className="col-12 md:col-6">
                                            <div
                                                className={`scope-card ${scope === 'network' ? 'selected' : ''}`}
                                                onClick={() => setScope('network')}
                                            >
                                                <div className="flex align-items-center gap-2">
                                                    <RadioButton checked={scope === 'network'} onChange={() => setScope('network')} />
                                                    <div className="flex-1">
                                                        <div className="flex align-items-center gap-2">
                                                            <i className="pi pi-users text-primary text-sm" />
                                                            <span className="font-semibold text-sm">My Network</span>
                                                        </div>
                                                        <div className="text-xs text-500 mt-1">Visible to network members only</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-6">
                                            <div
                                                className={`scope-card ${scope === 'public' ? 'selected' : ''}`}
                                                onClick={() => setScope('public')}
                                            >
                                                <div className="flex align-items-center gap-2">
                                                    <RadioButton checked={scope === 'public'} onChange={() => setScope('public')} />
                                                    <div className="flex-1">
                                                        <div className="flex align-items-center gap-2">
                                                            <i className="pi pi-globe text-green-600 text-sm" />
                                                            <span className="font-semibold text-sm">Public</span>
                                                        </div>
                                                        <div className="text-xs text-500 mt-1">Visible to all marketplace users</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Lead Commission */}
                                <div className="form-group">
                                    <label className="form-label required">Lead Commission (Commission)</label>
                                    <div className="surface-100 border-round p-2">
                                        <div className="flex justify-content-between align-items-center mb-2">
                                            <span className="text-xs text-500">Set the commission percentage</span>
                                            <span className="text-xl font-bold text-green-600">{parseFloat(commission.toFixed(3))}%</span>
                                        </div>
                                        <Slider
                                            value={commission}
                                            onChange={(e) => setCommission(e.value)}
                                            min={0.5}
                                            max={10}
                                            step={0.001}
                                            className="w-full mb-1"
                                        />
                                        <div className="flex justify-content-between text-xs text-500">
                                            <span>0.5%</span>
                                            <span>10%</span>
                                        </div>
                                        {/* Manual Entry */}
                                        <div className="flex align-items-center justify-content-center gap-2 mt-2 pt-2 border-top-1 border-200">
                                            <span className="text-500 text-xs">Or enter:</span>
                                            <InputNumber
                                                value={commission}
                                                onValueChange={(e) => setCommission(Math.min(10, Math.max(0.5, e.value || 0.5)))}
                                                mode="decimal"
                                                minFractionDigits={1}
                                                maxFractionDigits={3}
                                                min={0.5}
                                                max={10}
                                                suffix="%"
                                                inputClassName="text-center font-bold"
                                                inputStyle={{ width: '6rem', fontSize: '0.9375rem' }}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-help">
                                        <i className="pi pi-info-circle mr-1" />
                                        Est. fee: <strong className="text-green-600">₹{getEstimatedCommission().toLocaleString('en-IN')}</strong> (on {formatLoanAmount(getLeadLoanAmount(selectedLead))})
                                    </div>
                                </div>

                                {/* Max Collaborators */}
                                <div className="form-group">
                                    <label className="form-label required">Maximum Collaborators</label>
                                    <div className="quick-chips">
                                        {[1, 2, 3, 4, 5].map(num => (
                                            <button
                                                key={num}
                                                className={`quick-chip ${maxSpots === num ? 'active' : ''}`}
                                                onClick={() => setMaxSpots(num)}
                                            >
                                                {num}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="form-help">Number of agents who can collaborate on this lead</div>
                                </div>

                                {/* Expiry Date */}
                                <div className="form-group">
                                    <label className="form-label required">Listing Expiry</label>
                                    <div className="grid">
                                        <div className="col-12 md:col-6">
                                            <Calendar
                                                value={expiresAt}
                                                onChange={(e) => setExpiresAt(e.value)}
                                                minDate={new Date()}
                                                showIcon
                                                className="w-full"
                                                dateFormat="M dd, yy"
                                            />
                                        </div>
                                        <div className="col-12 md:col-6">
                                            <div className="quick-chips h-full align-items-center">
                                                {expiryOptions.map(opt => (
                                                    <button
                                                        key={opt.days}
                                                        className={`quick-chip ${getDaysUntilExpiry() === opt.days ? 'active' : ''}`}
                                                        onClick={() => setQuickExpiry(opt.days)}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Confirmations */}
                                <div className="form-group mb-0">
                                    <div className="flex align-items-start gap-2 mb-2 cursor-pointer" onClick={() => setDataAccurate(!dataAccurate)}>
                                        <Checkbox checked={dataAccurate} onChange={(e) => setDataAccurate(e.checked)} />
                                        <span className="text-700 text-sm">I confirm that the lead data is accurate and up-to-date</span>
                                    </div>
                                    <div className="flex align-items-start gap-2 cursor-pointer" onClick={() => setTermsAccepted(!termsAccepted)}>
                                        <Checkbox checked={termsAccepted} onChange={(e) => setTermsAccepted(e.checked)} />
                                        <span className="text-700 text-sm">I agree to the marketplace terms and conditions</span>
                                    </div>
                                </div>

                                {/* Navigation Buttons - Step 2 */}
                                <div className="nav-buttons-container flex justify-content-between">
                                    <Button
                                        label="Back"
                                        icon="pi pi-arrow-left"
                                        className="p-button-text"
                                        onClick={prevStep}
                                    />
                                    <Button
                                        label="Continue"
                                        icon="pi pi-arrow-right"
                                        iconPos="right"
                                        onClick={nextStep}
                                        disabled={!canProceed()}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {/* Step 3: Review */}
                    {activeStep === 2 && selectedLead && (
                        <>
                            <div className="surface-card border-round shadow-1 p-3 mb-3">
                                <h3 className="m-0 font-semibold mb-3">Review Your Listing</h3>

                                {/* Lead Details */}
                                <div className="mb-3">
                                    <h4 className="text-xs text-500 font-medium mb-2 mt-0">LEAD DETAILS</h4>
                                    <LeadSelectCard
                                        lead={selectedLead}
                                        isSelected={true}
                                        onSelect={() => {}}
                                    />
                                </div>

                                {/* Configuration Summary */}
                                <div className="mb-3">
                                    <h4 className="text-xs text-500 font-medium mb-2 mt-0">LISTING CONFIGURATION</h4>
                                    <div className="surface-100 border-round p-2">
                                        <div className="grid" style={{ rowGap: '0.75rem' }}>
                                            <div className="col-6 md:col-3">
                                                <div className="text-500 text-xs mb-1">Marketplace</div>
                                                <Tag
                                                    value={scope === 'network' ? 'Network' : 'Public'}
                                                    icon={`pi ${scope === 'network' ? 'pi-users' : 'pi-globe'}`}
                                                    severity={scope === 'network' ? 'info' : 'success'}
                                                    style={{ fontSize: '0.6875rem', padding: '0.2rem 0.5rem' }}
                                                />
                                            </div>
                                            <div className="col-6 md:col-3">
                                                <div className="text-500 text-xs mb-1">Lead Commission</div>
                                                <div className="font-bold text-green-600 text-base">{parseFloat(commission.toFixed(3))}%</div>
                                            </div>
                                            <div className="col-6 md:col-3">
                                                <div className="text-500 text-xs mb-1">Max Collaborators</div>
                                                <div className="font-bold text-900 text-base">{maxSpots}</div>
                                            </div>
                                            <div className="col-6 md:col-3">
                                                <div className="text-500 text-xs mb-1">Expires On</div>
                                                <div className="font-semibold text-700 text-sm">
                                                    {expiresAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Estimated Earnings */}
                                <div className="bg-green-50 border-round p-2">
                                    <div className="flex align-items-center justify-content-between flex-wrap gap-2">
                                        <div className="flex align-items-center gap-2">
                                            <i className="pi pi-wallet text-green-600 text-sm" />
                                            <span className="text-700 text-sm">Est. Lead Commission on Disbursement:</span>
                                        </div>
                                        <span className="font-bold text-green-700 text-base">
                                            ₹{getEstimatedCommission().toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                </div>

                                {/* Navigation Buttons - Step 3 */}
                                <div className="nav-buttons-container flex justify-content-between">
                                    <Button
                                        label="Back"
                                        icon="pi pi-arrow-left"
                                        className="p-button-text"
                                        onClick={prevStep}
                                    />
                                    <Button
                                        label="Post to Marketplace"
                                        icon="pi pi-check"
                                        onClick={handlePost}
                                        loading={posting}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                </div>

                {/* Summary Sidebar */}
                <div className="col-12 lg:col-4">
                    <SummarySidebar />
                </div>
            </div>
        </div>
    );
};

export default PostLead;
